import { useState, useMemo, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faBook,
  faTable,
  faFilter,
  faTimes,
  faCalendarAlt,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import TransactionView from "./TransactionView";
import CashBookView from "./CashBookView";
import AddTransaction from "./AddTransaction";
import EditTransaction from "./EditTransaction";
import TransactionDetails from "./TransactionDetails";
import { useTransactions } from "../../../hooks/useTransactions";
import { useAuth } from "../../../hooks";

const FinancialTab = ({ shopId }) => {
  const { hasPermission } = useAuth();

  const canCreate = hasPermission([
    "manage_all",
    "manage_financial_records",
    "manage_shop_modules",
  ]);

  const canUpdate = hasPermission([
    "manage_all",
    "manage_financial_records",
    "manage_shop_modules",
  ]);

  const {
    transactions,
    summary: hookSummary,
    pagination,
    searchTerm,
    setSearchTerm,
    categoryFilter,
    setCategoryFilter,
    typeFilter,
    setTypeFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    createTransaction,
    updateTransaction,
  } = useTransactions(shopId);

  const [activeView, setActiveView] = useState("transactions");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Advanced Filter Modal State
  const [isAdvFilterOpen, setIsAdvFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    startDate: "",
    endDate: "",
  });
  const [tempDateError, setTempDateError] = useState("");

  const formatDateShort = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDateRangeText = () => {
    if (isFilterApplied && startDate && endDate) {
      return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
    }
    return "All Records";
  };

  const handleTempStartDateChange = (value) => {
    setTempFilters((prev) => ({ ...prev, startDate: value }));
    setTempDateError("");
    if (
      tempFilters.endDate &&
      value &&
      new Date(value) > new Date(tempFilters.endDate)
    ) {
      setTempDateError("Start date cannot be after end date.");
    }
  };

  const handleTempEndDateChange = (value) => {
    setTempFilters((prev) => ({ ...prev, endDate: value }));
    setTempDateError("");
    if (
      tempFilters.startDate &&
      value &&
      new Date(value) < new Date(tempFilters.startDate)
    ) {
      setTempDateError("End date must be greater than or equal to start date.");
    }
  };

  const openAdvFilter = () => {
    setTempFilters({
      startDate: startDate || "",
      endDate: endDate || "",
    });
    setTempDateError("");
    setIsAdvFilterOpen(true);
  };

  const applyAdvFilters = () => {
    if (!tempFilters.startDate || !tempFilters.endDate) {
      setTempDateError("Please select both start date and end date.");
      return;
    }

    const start = new Date(tempFilters.startDate);
    const end = new Date(tempFilters.endDate);

    if (end < start) {
      setTempDateError("End date must be greater than or equal to start date.");
      return;
    }

    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setIsFilterApplied(true);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const clearAdvFilters = () => {
    setTempFilters({ startDate: "", endDate: "" });
    setTempDateError("");
    setStartDate("");
    setEndDate("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const categories = useMemo(() => {
    const cats = new Set();
    (transactions || []).forEach((t) => cats.add(t.category));
    return Array.from(cats);
  }, [transactions]);

  const summary = useMemo(() => {
    return {
      totalIn: hookSummary?.totalIn ?? 0,
      totalOut: hookSummary?.totalOut ?? 0,
      netBalance: hookSummary?.netBalance ?? 0,
      breakdown: hookSummary?.breakdown ?? {},
      totalTransactions: hookSummary?.totalTransactions ?? 0,
    };
  }, [hookSummary]);

  const totalItems = pagination?.total ?? 0;
  const totalPages = pagination?.last_page ?? 1;
  const startIndex = (currentPage - 1) * pageSize;
  const currentData = transactions;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    categoryFilter,
    typeFilter,
    pageSize,
    isFilterApplied,
    setCurrentPage,
  ]);

  const formatCurrency = (amount) => {
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " TSH"
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      Capital: "bg-blue-900 text-white border-blue-900",
      Sales: "bg-green-700 text-white border-green-700",
      Purchase: "bg-orange-600 text-white border-orange-600",
      Expense: "bg-red-600 text-white border-red-600",
      Receive: "bg-purple-700 text-white border-purple-700",
    };
    return colors[category] || "bg-gray-600 text-white border-gray-600";
  };

  const handleAddTransaction = async (data) => {
    if (!canCreate) return;
    try {
      await createTransaction({
        shopId,
        category: (data.category || "").toLowerCase(),
        amount: Number(data.amount),
        added_date: data.date,
        description: data.description,
      });
      setAddModalOpen(false);
    } catch (err) {
      console.error("Failed to create transaction", err);
    }
  };

  const handleEditTransaction = async (data) => {
    if (!canUpdate) return;
    try {
      await updateTransaction(data.id, {
        category: (data.category || "").toLowerCase(),
        amount: Number(data.amount),
        added_date: data.date,
        description: data.description,
      });
      setEditModalOpen(false);
      setSelectedTransaction(null);
    } catch (err) {
      console.error("Failed to update transaction", err);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Financial records
          </h3>
          <p className="text-sm text-gray-600">
            Track all money in and out of the shop
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setAddModalOpen(true)}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add records
          </Button>
        )}
      </div>

      {/* Main Toolbar: Advanced Filter Button only (Search is inside child views) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-600">Showing:</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs">
            <FontAwesomeIcon icon={faCalendarAlt} className="h-3 w-3" />
            {getDateRangeText()}
          </span>
          <span className="text-gray-500">|</span>
          <span className="text-gray-600">
            <strong>{totalItems}</strong> transactions found
          </span>
        </div>

        <Button
          onClick={openAdvFilter}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6"
        >
          <FontAwesomeIcon icon={faFilter} />
          Advanced Filter
          {isFilterApplied && (
            <span className="ml-1 w-2 h-2 rounded-full bg-orange-400"></span>
          )}
        </Button>
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveView("transactions")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeView === "transactions"
              ? "border-blue-800 text-blue-800"
              : "border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <FontAwesomeIcon icon={faTable} className="h-4 w-4" />
          Transaction
        </button>
        <button
          onClick={() => setActiveView("cashbook")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeView === "cashbook"
              ? "border-blue-800 text-blue-800"
              : "border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <FontAwesomeIcon icon={faBook} className="h-4 w-4" />
          Cash book
        </button>
      </div>

      {activeView === "cashbook" ? (
        <CashBookView
          transactions={transactions}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getCategoryColor={getCategoryColor}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          categories={categories}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          currentData={currentData}
          startIndex={startIndex}
        />
      ) : (
        <TransactionView
          transactions={transactions}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getCategoryColor={getCategoryColor}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          categories={categories}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          currentData={currentData}
          startIndex={startIndex}
          summary={summary}
          setSelectedTransaction={setSelectedTransaction}
          setViewModalOpen={setViewModalOpen}
          setEditModalOpen={setEditModalOpen}
          canUpdate={canUpdate}
        />
      )}

      {canCreate && (
        <AddTransaction
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSave={handleAddTransaction}
        />
      )}

      {canUpdate && (
        <EditTransaction
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedTransaction(null);
          }}
          onSave={handleEditTransaction}
          transaction={selectedTransaction}
        />
      )}

      <TransactionDetails
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        onEdit={() => {
          setViewModalOpen(false);
          setEditModalOpen(true);
        }}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        getCategoryColor={getCategoryColor}
        canUpdate={canUpdate}
      />

      {/* Advanced Filter Modal */}
      {isAdvFilterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Advanced Filter
              </h3>
              <button
                onClick={() => setIsAdvFilterOpen(false)}
                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-blue-600 h-4 w-4"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Filter Transactions
                  </h4>
                  <p className="text-xs text-gray-600">
                    Select criteria to narrow down results
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Filter by Date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      From
                    </label>
                    <input
                      type="date"
                      value={tempFilters.startDate}
                      onChange={(e) =>
                        handleTempStartDateChange(e.target.value)
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      To
                    </label>
                    <input
                      type="date"
                      value={tempFilters.endDate}
                      onChange={(e) => handleTempEndDateChange(e.target.value)}
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>

              {tempDateError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="h-3.5 w-3.5 text-red-500"
                  />
                  <span>{tempDateError}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={clearAdvFilters}
                className="border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none px-6"
              >
                Clear All
              </Button>
              <Button
                onClick={applyAdvFilters}
                disabled={!!tempDateError}
                className="bg-blue-800 hover:bg-blue-900 text-white px-6 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialTab;
